<template>
  <div
    class="chat-box-container"
    data-cy="chat-box"
    :class="[
    showChat ? 'expanded' : '',
    !themeStore.hideChatBotPopup ? 'animated-btn' : '',
    !store.isChatButtonShowing ? 'chat-btn-hidden' : '',
  ]">
    <button
      type="button"
      class="btn k-chat-show-btn"
      data-cy="chat-visibility-toggle"
      :title="chatButtonToggleLabel"
      :aria-label="chatButtonToggleLabel"
      @click="toggleShowChatButton"
    >
      <i :class="chatButtonIconClass" aria-hidden="true"></i>
    </button>
    <div class="k-chat-btn-row">
      <button
        type="button"
        class="btn btn-primary k-chat-btn"
        data-cy="chat-toggle-button"
        title="Toggle"
        @click="toggleChat"
        aria-label="Toggle chat button"
      >
        <img src="../../../img/icons/kate-bot-chat-colours.svg" alt="KATE bot avatar"/>
      </button>
    </div>
    <div class="chat-box-content" data-cy="chat-panel">
      <div class="chat-box-header">
        <h3>KATE Learning Assistant</h3>
        <router-link
          data-cy="chat-open-fullscreen"
          :to="{ name: 'chatbot_page', params: { programmeId: programmeId, sessionId: sessionId } }"
          target="_blank"
          class="btn btn-icon"
          aria-label="Open chat in a new tab"
          title="Open chat in a new tab"
        >
          <i class="fas fa-external-link-alt acuity" aria-hidden="true"></i>
        </router-link>
      </div>
      <k-chat-msg :message="welcomeMessage" :showActions="false"></k-chat-msg>
      <k-chat-msg
          v-for="(message, index) in messages"
          :key="message.chatbot_query_feedback_id || index"
          :class='["message", { right: !message.fromChatbot }]'
          :message="message"
          :showActions="showActionsForMessages(message)"
          @update-rating="(rating) => updateRating(index, rating)"
      />
      <k-chat-msg v-if="isLoadingReply"
        :loading="isLoadingReply"
        :class='["message", { right: false }]'
        :message="{text: 'Loading...', fromChatbot: true}"
        ></k-chat-msg>
      <div class="chat-box-input-area">
        <k-ai-persistent-disclosure placement="above-input" />
        <form class="message-input-container" :disabled="isDisabled">
        <label for="k-chatbox-input">
          <input
            id="k-chatbox-input"
            data-cy="chat-input"
            class="form-control message-input"
            v-model="text"
            type="input"
            placeholder="Type message..."
            :maxlength="maxQueryLength"
          >
        </label>
        <button
          :disabled='text === "" || isDisabled'
          type="submit"
          class="submit-button"
          data-cy="chat-submit"
          @click.prevent="postQuery"
        >Send</button>
        </form>
      </div>
    </div>
    <k-popup v-if="!themeStore.hideChatBotPopup"
      @close="closePopup"
      :customClass="'info-popup'"
      :show="!themeStore.hideChatBotPopup"
      :title="'Hi there, I\'m KATE!'"
      :position="['top', 'left', 'bottom-left']"
      :body="`How can I help you today?`">
    </k-popup>
  </div>
</template>

<style scoped>
.chat-box-container {
  position: fixed;
  right: -10px;
  bottom: 0;
  z-index: 9999;
  padding: 10px 20px 20px;
}

.chat-box-container .chat-box-content {
  transition: right 0.2s ease-in-out;
  background-color: var(--chat-background);
  border: var(--kate-chat-border);
  position: fixed;
  margin-left: auto;
  width: 350px;
  right: -399px;
  bottom: 87px;
  display: flex;
  max-height: 55vh;
  overflow: auto;
  flex-direction: column;
  border-radius: 10px 10px 0;
  z-index: 1;
}

.chat-box-input-area {
  position: sticky;
  bottom: 0;
  padding-bottom: 4px;
  background-color: var(--chat-background);
}

.chat-box-input-area .ai-persistent-disclosure--above-input {
  padding-top: 4px;
}

.chat-box-container.expanded .chat-box-content {
  pointer-events: auto;
  box-shadow: var(--box-shadow-alt);
  right: 20px;
  max-height: 55vh;
  overflow: auto;
  transition: right 0.2s ease-in-out;
}

.chat-box-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0 10px 15px;
  margin-bottom: 10px;
  background-color: var(--kate-panel);
  position: sticky;
  top: 0;
  z-index: 1;
}

.chat-box-header a {
  font-size: 1.1rem;
}

.chat-box-header a:hover,
.chat-box-header a:focus {
  color: var(--kate-type-link);
}

form.message-input-container {
  display: flex;
  align-items: center;
  -webkit-box-flex: 0;
  flex: 0 1 42px;
  border-radius: 8px 0 0;
  justify-content: space-between;
  border: var(--kate-chat-alt-border);
  background-color: var(--chat-background);
  width: 100%;
}

form.message-input-container label {
  flex: 1;
}

.message-input {
  background: none;
  border: none;
  outline: none !important;
  resize: none;
  margin: 0;
  display: flex;
  overflow: hidden;
  border-radius: 8px 0 0;
  box-shadow: unset;
  color: var(--kate-type-primary);
  flex-grow: 1;
  min-height: 40px;
  padding-left: 15px;
}

.submit-button {
  background-color: transparent;
  padding: 8px 15px;
}

.submit-button:hover:disabled,
.submit-button:disabled {
  color: var(--kate-disabled);
  background-color: transparent;
  text-decoration: none;
}

.submit-button:hover {
  text-decoration: underline;
  color: var(--kate-type-accent);
}

.k-chat-btn-row {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.k-chat-hide-btn,
.k-chat-show-btn {
  display: none !important;
}

.chat-box-container.expanded .k-chat-btn {
  border-radius: 50% 0 50% 50%;
}

.chat-box-container .k-chat-btn {
  color: var(--kate-type-dark);
  box-shadow: -2px 2px 3px rgb(0 0 0 / 0.5);
  border: var(--kate-chat-border);
  background-color: var(--kate-background);
  padding: 8px;
  position: relative;
  z-index: 1;
  border-radius: 50% 50% 0;
}

.animated-btn .k-chat-btn {
  animation: double-pulse 3s ease-in-out;
}

.chat-box-container .k-chat-btn:focus {
  outline: var(--kate-chat-border);
  box-shadow: 0 0 15px 2px var(--kate-primary);
}

.chat-box-container .k-chat-btn img {
  width: 50px;
}

@keyframes double-pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: -2px 2px 3px rgb(0 0 0 / 0.5);
  }

  20%,
  60% {
    transform: scale(1.1);
    box-shadow: 0 0 15px 2px var(--kate-primary);
  }

  40%,
  80% {
    transform: scale(1);
    box-shadow: -2px 2px 3px rgb(0 0 0 / 0.5);
  }
}

@media (min-width: 768px) {
  .k-chat-show-btn {
    display: none !important;
    border-radius: 8px 8px 0 0;
  }
}

/* Slim chat control for small screens / magnification users */
@media (max-width: 767px) {
  .chat-box-container {
    right: 0;
    bottom: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .k-chat-hide-btn,
  .k-chat-show-btn {
    display: flex !important;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    min-height: 44px;
    font-size: 22px;
    padding: 10px 14px;
    line-height: 1.2;
    border: none;
    background-color: transparent;
    color: var(--kate-neon-primary-text);
    text-shadow: var(--kate-neon-primary-shadow);
    z-index: 2;
  }

  .k-chat-hide-btn:hover,
  .k-chat-show-btn:hover {
    background-color: var(--kate-panel-alt);
  }

  .k-chat-hide-btn {
    border-bottom: none;
    border-radius: 8px 8px 0 0;
  }

  .chat-box-container.chat-btn-hidden .k-chat-show-btn {
    display: flex !important;
  }

  .chat-box-container.chat-btn-hidden .k-chat-btn-row {
    display: none;
  }

  .chat-box-container .k-chat-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    min-height: 44px;
    padding: 8px 14px;
    border-radius: 20px 20px 0;
    box-shadow: -1px 1px 2px rgb(0 0 0 / 0.35);
  }

  .chat-box-container.expanded .k-chat-btn {
    border-radius: 20px 0 20px 20px;
  }

  .chat-box-container .k-chat-btn img {
    width: 28px;
  }

  .animated-btn .k-chat-btn {
    animation: none;
  }

  .chat-box-container .chat-box-content {
    width: min(350px, calc(100vw - 20px));
    right: -100%;
    bottom: 88px;
    border-radius: 20px 20px 0 0;
  }

  .chat-box-container.expanded .chat-box-content {
    right: 10px;
    max-height: 50vh;
  }

  .chat-box-container.chat-btn-hidden .chat-box-content,
  .chat-box-container.chat-btn-hidden.expanded .chat-box-content {
    right: -100%;
    pointer-events: none;
  }
}

/* Very constrained viewports (e.g. high magnification ~320x256) */
@media (max-width: 480px) and (max-height: 300px) {
  .k-chat-hide-btn,
  .k-chat-show-btn {
    min-width: 0;
    min-height: 0;
    padding: 2px 8px;
    font-size: 14px;
  }

  .chat-box-container .k-chat-btn {
    min-width: 0;
    min-height: 0;
    padding: 4px 8px;
  }

  .chat-box-container .k-chat-btn img {
    width: 22px;
  }

  .chat-box-container .chat-box-content {
    bottom: 52px;
  }
}
</style>

<script>
import useGlobalStore from '@stores/global';
import useThemeStore from '@stores/theme';
import StorageMixin from '@mixins/storage-mixins';
import { uuid } from '@utils/uuid-wrapper';
import KChatMsg from './k-chat-msg.vue';
import KPopup from '../k-popup.vue';
import KAiPersistentDisclosure from '../ai/k-ai-persistent-disclosure.vue';

export default {

  components: {
    KChatMsg,
    KPopup,
    KAiPersistentDisclosure,
  },

  mixins: [StorageMixin],

  props: {
    programmeId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      store: useGlobalStore(),
      themeStore: useThemeStore(),
      showChat: false,
      text: '',
      chatbotResponse: undefined,
      welcomeMessage:
        {
          text: 'Hi there, I\'m KATE! I can answer questions about your programme, explain concepts, help you with a coding task and more. <br />What can I help you with today?',
          fromChatbot: true,
        },
      messages: [],
      isLoadingReply: false,
      isDisabled: false,
      sessionId: uuid(),
      defaultErrorMessage: 'There was an error getting a response from the Chatbot. Please try again later.',
      maxQueryLength: 2048,
    };
  },

  beforeMount() {
    const startingState = this.loadSetting('chatbot-toggle-button', 'isChatButtonShowing');
    if (startingState !== undefined) {
      this.store.setChatButton(startingState);
    }
  },

  computed: {
    isChatButtonShowing() {
      return this.store.isChatButtonShowing;
    },

    chatButtonIconClass() {
      return this.store.chatButtonIconClass;
    },

    chatButtonToggleLabel() {
      return this.isChatButtonShowing ? 'Hide chat button' : 'Show chat button';
    },
  },

  methods: {
    closePopup() {
      this.themeStore.$patch({ hideChatBotPopup: true });
      this.updateFrontendSettings();
    },

    toggleChat() {
      this.showChat = !this.showChat;
    },

    toggleShowChatButton() {
      this.store.toggleChatButton();
      if (!this.store.isChatButtonShowing) {
        this.showChat = false;
      }
      this.saveSetting('chatbot-toggle-button', 'isChatButtonShowing', this.store.isChatButtonShowing);
    },

    showActionsForMessages(message) {
      if (message.isError) {
        return false;
      }
      return true;
    },

    updateFrontendSettings() {
      return this.$http.put('/api/profile/user/frontend_settings', {
        theme: this.themeStore.theme,
        hide_themes_popup: this.themeStore.hideThemesPopup,
        hide_chatbot_popup: this.themeStore.hideChatBotPopup,
      }).then(() => {
        this.$logger.info('User preference saved');
      }).catch(err => {
        this.$logger.warn('Error saving user preference for user', undefined, err);
      });
    },

    updateRating(index, rating) {
      this.messages[index].rating = rating;
    },

    postQuery() {
      this.isDisabled = true;
      this.messages.push({
        text: this.text,
        fromChatbot: false,
        rating: null,
      });
      this.$logger.info('Posting query to chatbot', { programmeId: this.programmeId }, true);
      const payload = {
        query: this.text,
        page_url: this.$route.path,
        session_id: this.sessionId,
        history: this.messages.slice(0, -1).map(message => ({
          source: message.fromChatbot ? 'chatbot' : 'human',
          content: message.text,
        })),
      };
      this.isLoadingReply = true;
      this.text = '';
      this.$http.post(`/api/chatbot/query/${this.programmeId}`, payload).then(result => {
        this.chatbotResponse = result.data;
        this.messages.push({
          ...this.chatbotResponse,
          text: this.chatbotResponse?.answer,
          fromChatbot: true,
        });
        this.$logger.info('Successfully posted query', { programmeId: this.programmeId });
      }).catch(err => {
        const errorMessage = err.response?.status === 429 ? 'KATE is receiving a lot of requests right now. Please try again later.' : this.defaultErrorMessage;
        this.$logger.error('Error posting query', { programmeId: this.programmeId }, err);
        this.messages.push({
          text: errorMessage,
          isError: true,
          fromChatbot: true,
        });
      }).then(() => {
        this.isLoadingReply = false;
        this.isDisabled = false;
      });
    },
  },
};
</script>
